import React, { Fragment, useState, useCallback } from 'react';
import { List, ListSubheader, Icon, Button, Tooltip, Select, MenuItem } from '@mui/material';
import ScoreIcon from 'global/components/ScoreIcon';
import ChecklistScores from '../ChecklistScores';
import { SystemBlock, SystemHeader, HeaderChild, HeaderChildScore, HeaderChildName, HeaderCriteria, ToggleSidebar, ShowImages } from './styles';
import { get, last, size, uniqBy } from 'global/lib/lodash';
import { getItemKey, getId } from './utils';
import Modal from 'global/components/modal';
import useRetrieveData from 'global/hooks/useRetrieveData';
import ImageGrid from 'global/components/image-grid';
import { Button as Btn } from 'global/components/Button';
import { useTheme } from '@mui/material/styles';
import { stopProp, _size, sortAlphaNum } from 'global/utils';
import ReportPhotos from '../ReportPhotos';
import { withRouter } from 'react-router-dom';
import { useSupersets } from 'global/hooks/useSupersets';
import { Text } from 'global/components/text';

// PFM-2654: Hashed/logged-out viewers of a shared assessment can't hit the
// /supersets endpoint (backend blocks it for non-authenticated sessions), so the
// useSupersets hook returns an empty list and the photos modal's System filter
// dropdown ends up empty. Derive the same list directly from the already-loaded
// flat processed-scorecard-score-values: superset-level rows are depth === 1 and
// carry a supersetId. This produces the identical { id, name } shape the dropdown
// expects, so logged-in users (who get a non-empty fetched list) are unaffected.
export const supersetsFromScores = (scores) =>
  uniqBy(
    (scores || []).filter((s) => get(s, 'depth') === 1 && get(s, 'supersetId') != null).map((s) => ({ id: String(get(s, 'supersetId')), name: get(s, 'name') })),
    'id'
  );

export const ImageModal = (props) => {
  const theme = useTheme();
  const { superset_id, assessment } = props;

  const [state, setState] = useState({
    superset_id,
  });

  const { supersets: fetchedSupersets } = useSupersets({ assessment });
  const supersets = size(fetchedSupersets) ? fetchedSupersets : supersetsFromScores(get(props, 'scores', []));

  const { data, loading, error } = useRetrieveData(
    { url: '/realm-photos' },
    {
      params: {
        filter: {
          superset_id: state.superset_id,
          assessment_id: get(assessment, 'id'),
        },
        include: 'supersets,images',
      },
      build: ['realmPhotos'],
      useEffect: true,
      transformData: (da) => {
        da = da.map((d) => ({ ...d, ...get(d, 'images.0', {}), assessment })).sort(sortAlphaNum);
        if (!state.superset_id) {
          return da.filter((d) => !size(d.supersets));
        }
        return da;
      },
    }
  );

  if (error) {
    return <div style={{ color: theme.palette.error.main }}>{error}</div>;
  }

  return (
    <div>
      <Select displayEmpty style={{ width: '100%' }} value={state.superset_id} onChange={(e) => setState((s) => ({ ...s, superset_id: e.target.value }))}>
        {[{ name: 'None', value: null }, ...supersets].map((s) => (
          <MenuItem key={s.id} value={s.id}>
            {s.name}
          </MenuItem>
        ))}
      </Select>
      <ImageGrid
        folders={data}
        includeSearch={false}
        loading={loading}
        userCanEdit={false}
        hideSuperset
        blankContent={() => <Text>There are no photos here; try changing the filters.</Text>}
        {...props}
      />
    </div>
  );
};

export const ImageSelectModal = withRouter((props) => {
  const [selected, setSelected] = useState({});

  const onSelectedChange = (sel) => {
    setSelected(
      Object.keys(sel)
        .filter((k) => !!sel[k])
        .map((k) => sel[k])
    );
  };

  const onClose = () => {
    props.onClose();
    setSelected({});
  };

  const onSubmit = () => {
    props.onSubmit && props.onSubmit({ images: selected });
    onClose();
  };

  const renderBtn = () => {
    if (!_size(selected)) {
      return { title: 'Add', disabled: true };
    }

    return {
      title: `Add ${_size(selected)}`,
      onClick: onSubmit,
    };
  };

  const IMAGEMODAL = props.useFullPhotos ? ReportPhotos : ImageModal;

  return (
    <Modal {...props} onClose={onClose} buttons={[renderBtn()]}>
      <IMAGEMODAL canSelect={true} {...props} _location={{ pathname: 'reports/photo' }} onSelectedChange={onSelectedChange} />
    </Modal>
  );
});

export const ImageSelectBtn = (props) => {
  const [modal, setModal] = useState({ open: false });

  const showImages = (e) => {
    stopProp(e);
    props.onOpen && props.onOpen();
    setModal({
      ...props,
      open: true,
      fullScreen: true,
      title: props.modalTitle || `Photos`,
      maxWidth: 920,
      onClose: () => {
        setModal((m) => ({ ...m, open: false }));
        props.onClose && props.onClose();
      },
    });
  };

  if (!props.useFullPhotos && get(props, 'assessment.realmPhotoCount', 0) === 0) {
    return null;
  }

  return (
    <>
      <Tooltip title={props.tooltip ? props.tooltip : props.disabled ? 'Please select a system to access your photos' : 'Insert from media already uploaded to the Photos tab'}>
        <span>
          <Btn {...props} onClick={showImages}>
            <Icon>photo_library</Icon>
          </Btn>
        </span>
      </Tooltip>
      <ImageSelectModal {...modal} />
    </>
  );
};

export function SystemScore(props) {
  const { pssvSystem, setSidebarOpen, activeEdit, pssvChecklist, selectItem, selectedItems, showSelectBoxes, isCourse, notFirst, printView, hideSystemHeader, bordered, itemPasses } = props;
  const pChildren = get(pssvSystem, 'children', []).filter((f) => (activeEdit ? getId(pssvChecklist) === getId(f) : true));
  const isSelected = useCallback((item) => get(selectedItems, getItemKey(item)), [selectedItems]);
  const hasSelectedItems = Object.keys(selectedItems).filter((k) => get(selectedItems, [k, 'itemType']) === 'checkpoint').length > 0;

  const currentSelect = isSelected(pssvSystem);
  const indeterminate = Object.keys(selectedItems).filter((k) => get(selectedItems, [k, 'parentId']) == pssvSystem?.id).length > 0;

  const useCompareScore = get(props, 'filter.assessment_status') !== 'active';
  const useCompare = useCompareScore && get(props, 'filter.useDiff');

  const ScoreIconProps = {
    ...(!activeEdit
      ? {
          scorecard: props.scorecard,
          useBlankCheckbox: printView ? false : showSelectBoxes,
          selected: !printView && showSelectBoxes && get(currentSelect, 'fullCount'),
          indeterminate: !printView && showSelectBoxes && indeterminate,
          onClick: printView ? undefined : () => selectItem(pssvSystem),
          useCompare,
          compare: get(pssvSystem, 'compare', null),
          displayValue: get(pssvSystem, 'displayValue', null),
          canClick: false,
          filter: get(props, 'filter'),
        }
      : {}),

    score: useCompareScore && !useCompare ? get(pssvSystem, 'compare.value') : get(pssvSystem, 'value'),
    active: useCompareScore && !useCompare ? get(pssvSystem, 'compare') : pssvSystem,
  };

  const RootWrap = notFirst ? SystemBlock : 'div';

  return (
    <RootWrap key={getId(pssvSystem)}>
      {!isCourse && !hideSystemHeader && (
        <SystemHeader as={ListSubheader} disableGutters={true}>
          <ScoreIcon bordered={bordered} actionable={itemPasses?.(pssvSystem)} {...ScoreIconProps} />
          {!useCompare && <HeaderChildScore as="h2">{get(ScoreIconProps, 'active.displayValue')}</HeaderChildScore>}
          <HeaderChild as="h2">
            <HeaderChildName>{get(pssvSystem, 'name')}</HeaderChildName>
            {get(pssvSystem, 'criteria') && <HeaderCriteria className="weight-criteria">{get(pssvSystem, 'criteria')}</HeaderCriteria>}
          </HeaderChild>
          <div style={{ flexGrow: 1 }}></div>
          {!printView && (
            <ToggleSidebar as={Button} onClick={() => setSidebarOpen(true)}>
              <Icon>view_list</Icon>
            </ToggleSidebar>
          )}

          {!printView && (
            <ShowImages
              as={ImageSelectBtn}
              variant="text"
              color="secondary"
              tooltip={`${get(pssvSystem, 'name')} Photos`}
              superset_id={get(pssvSystem, 'supersetId')}
              assessment={get(props, 'assessment.data')}
              scores={get(props, 'allScoreData', [])}
              canSelect={false}
            />
          )}
        </SystemHeader>
      )}
      <List>
        {pChildren.map((data, i) => (
          <Fragment key={data.id}>
            <ChecklistScores
              {...props}
              pssvChecklist={data}
              isSelected={isSelected}
              hasSelectedItems={hasSelectedItems}
              next={(activeEdit && props.next) || get(pChildren, i + 1, get(props, 'next.children.0'))}
              prev={(activeEdit && props.prev) || get(pChildren, i - 1, last(get(props, 'prev.children')))}
              hideSystemHeader={hideSystemHeader}
            />
          </Fragment>
        ))}
      </List>
    </RootWrap>
  );
}
